/**
 * Company Search — engagements / delivery history (sealed element f).
 *
 * Titled "Engagements", not "Active engagements" (Robert-ruled amendment):
 * engagement currency is not reliably known here, so "Active" would be a false
 * behavioral claim. For the same reason the relationship band no longer shows
 * an Active/Dormant tile (see RelationshipBand); it now reports Client history
 * (Yes/No) only.
 *
 * Annotation 2 GOVERNS the row shape: exactly three sanitized fields —
 * headline/descriptor, case-study yes/no, and Value Tier. Active/Dormant is
 * NOT shown on the row (and no longer on the band) and the free-text snippet
 * is dropped.
 * Per-engagement freshness ("Last interaction") is a contract gap and absent,
 * not fabricated. The list shows the first five rows; "View more" reveals five
 * more per activation until exhausted, then disappears. Once expanded, "Show
 * less" collapses back to the first five and resets the reveal count — it stays
 * available even after exhaustion. The header count is the full total
 * regardless of how many rows are revealed.
 */

import { useState } from "react";
import { canCollapseReveal, hasMoreToReveal, nextRevealCount } from "../logic";
import type { EngagementView } from "../types";

const PAGE_SIZE = 5;

export function Engagements({ engagements }: { engagements: EngagementView[] }): JSX.Element {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const visible = engagements.slice(0, visibleCount);
  const canRevealMore = hasMoreToReveal(visibleCount, engagements.length);
  const canCollapse = canCollapseReveal(visibleCount, PAGE_SIZE);

  return (
    <section className="sxco-card">
      <p className="sxco-card-head">
        Engagements
        {engagements.length > 0 && <span className="sxco-count">{engagements.length}</span>}
      </p>
      {engagements.length === 0 ? (
        <p className="sxco-muted">No engagements on record for this company.</p>
      ) : (
        <>
          <ul className="sxco-engagements">
            {visible.map((e, i) => (
              <li key={`${e.practice ?? "engagement"}-${i}`} className="sxco-engagement">
                <p className="sxco-engagement-practice">{e.practice ?? "—"}</p>
                {e.descriptor && <p className="sxco-engagement-desc">{e.descriptor}</p>}
                <div className="sxco-engagement-tags">
                  {e.valueTier && (
                    <span className="sxco-chip sxco-chip--sm">{e.valueTier}</span>
                  )}
                  <span className="sxco-chip sxco-chip--sm sxco-chip--muted">
                    {e.caseStudyStatus === "approved" ? "Case study: Yes" : "Case study: No"}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          {(canRevealMore || canCollapse) && (
            <div className="sxco-eng-actions">
              {canRevealMore && (
                <button
                  type="button"
                  className="sxco-linkbtn"
                  onClick={() =>
                    setVisibleCount((c) => nextRevealCount(c, engagements.length, PAGE_SIZE))
                  }
                >
                  View more
                </button>
              )}
              {canCollapse && (
                <button
                  type="button"
                  className="sxco-linkbtn"
                  onClick={() => setVisibleCount(PAGE_SIZE)}
                >
                  Show less
                </button>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
